import React, { useRef, useState } from "react";
import api from "../lib/api";
import { X, Upload, CheckCircle2, AlertTriangle, FileText, ArrowLeft } from "lucide-react";

/**
 * Two-stage CSV upload modal.
 *
 *   stage 1 — pick: user chooses a file
 *   stage 2 — preview: app calls `endpoint?dry_run=true` and shows parsed
 *             rows, detected columns, date range, and would-be
 *             create / update / skip counts. Nothing has been written yet.
 *   stage 3 — result: user clicks "Commit import", app calls `endpoint`
 *             (no dry_run), shows final counts.
 *
 * The preview path never writes; the commit reuses the existing safe-merge
 * import endpoint. If the file is unparseable the preview surfaces the
 * errors inline without raising.
 */

function MiniCounter({ label, value, color, testid }) {
  return (
    <div className="border border-border rounded-md p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-heading text-2xl mt-0.5 tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function PreviewTable({ rows, columns }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic text-center py-6">
        No rows parsed — check the file format above.
      </div>
    );
  }
  // Render the first N rows as a proper HTML table. Columns come from the
  // first row's keys (already normalized by the backend parser).
  const cols = columns && columns.length > 0 ? columns : Object.keys(rows[0]);
  return (
    <div className="overflow-auto max-h-64 border border-border rounded-md" data-testid="csv-preview-table">
      <table className="w-full text-[11px]">
        <thead className="bg-secondary/40 sticky top-0">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left px-3 py-2 font-medium text-muted-foreground uppercase tracking-wider text-[10px]">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {cols.map((c) => {
                const v = r[c];
                return (
                  <td key={c} className="px-3 py-1.5 font-mono text-[11px] text-foreground/90 tabular-nums">
                    {v == null ? <span className="text-muted-foreground/50">—</span> : String(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CsvImportModal({ open, onClose, title, description, endpoint, onSuccess }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState("pick"); // pick | preview | result
  const [preview, setPreview] = useState(null); // response from dry_run
  const [result, setResult] = useState(null);   // response from commit
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setError(null); setBusy(false); setStage("pick");
  };
  const close = () => { reset(); onClose(); };

  if (!open) return null;

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`${endpoint}?dry_run=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStage("preview");
    } catch (e) {
      setError(e.userMessage || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStage("result");
      onSuccess?.(data);
    } catch (e) {
      setError(e.userMessage || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="csv-modal">
      <div className="w-full max-w-2xl bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h3 className="font-heading text-lg font-semibold">{title}</h3>
            {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
          </div>
          <button onClick={close} className="p-1.5 rounded-md hover:bg-secondary" data-testid="csv-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* -------- Stage 1: pick file -------- */}
          {stage === "pick" && (
            <>
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full border border-dashed border-border rounded-xl p-6 hover:border-zinc-600 transition-colors text-center"
                data-testid="csv-modal-pick"
              >
                <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
                <div className="mt-2 text-sm">{file ? file.name : "Click to choose a CSV file"}</div>
                <div className="text-xs text-muted-foreground mt-0.5">.csv up to 10 MB · auto-detect delimiter & date column · preview before commit</div>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setError(null); }}
                className="hidden"
                data-testid="csv-modal-file"
              />
              {error && (
                <div className="flex items-start gap-2 text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span data-testid="csv-modal-error">{error}</span>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={close} className="px-4 py-2 rounded-md text-sm border border-border hover:bg-secondary" data-testid="csv-modal-cancel">
                  Cancel
                </button>
                <button
                  onClick={runPreview}
                  disabled={!file || busy}
                  className="px-4 py-2 rounded-md text-sm bg-foreground text-background font-medium hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="csv-modal-preview"
                >
                  {busy ? "Parsing…" : "Preview"}
                </button>
              </div>
            </>
          )}

          {/* -------- Stage 2: preview -------- */}
          {stage === "preview" && preview && (
            <div className="space-y-3" data-testid="csv-preview-view">
              {/* Parse errors block the commit */}
              {preview.errors && preview.errors.length > 0 && (
                <div className="flex items-start gap-2 text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-3" data-testid="csv-preview-errors">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">Can&apos;t import this file:</div>
                    <ul className="list-disc list-inside mt-1 text-xs">
                      {preview.errors.map((msg, i) => <li key={i}>{msg}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              {/* Summary strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MiniCounter label="Parsed rows" value={preview.rows_total ?? 0} color="#FAFAFA" testid="csv-stat-total" />
                <MiniCounter label="Create" value={preview.rows_created || 0} color="#10B981" testid="csv-stat-create" />
                <MiniCounter label="Update" value={preview.rows_updated || 0} color="#3B82F6" testid="csv-stat-update" />
                <MiniCounter label="Skip" value={preview.rows_skipped || 0} color="#71717A" testid="csv-stat-skip" />
              </div>

              {/* Meta line */}
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1" data-testid="csv-preview-meta">
                {preview.date_column && (<span>date column · <span className="text-foreground">{preview.date_column}</span></span>)}
                {preview.date_range?.start && (
                  <span>range · <span className="text-foreground">{preview.date_range.start} → {preview.date_range.end}</span></span>
                )}
                {preview.cumulative && (<span className="text-[#F59E0B]">cumulative → deltas</span>)}
                {preview.columns_detected?.length > 0 && (
                  <span>columns · <span className="text-foreground">{preview.columns_detected.join(", ")}</span></span>
                )}
              </div>

              {/* Warnings */}
              {preview.warnings && preview.warnings.length > 0 && (
                <div className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-md p-2.5 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>{preview.warnings.join(" · ")}</div>
                </div>
              )}

              {/* First N rows */}
              <div>
                <div className="label-tiny mb-1.5 flex items-center gap-1.5"><FileText className="w-3 h-3" /> First {preview.preview_rows?.length || 0} row(s)</div>
                <PreviewTable rows={preview.preview_rows || []} columns={preview.columns_detected} />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span data-testid="csv-modal-error">{error}</span>
                </div>
              )}

              <div className="flex justify-between gap-2 pt-1">
                <button
                  onClick={() => { setPreview(null); setStage("pick"); }}
                  className="px-3 py-2 rounded-md text-sm border border-border hover:bg-secondary inline-flex items-center gap-1.5"
                  data-testid="csv-modal-back"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <div className="flex gap-2">
                  <button onClick={close} className="px-4 py-2 rounded-md text-sm border border-border hover:bg-secondary" data-testid="csv-modal-cancel">
                    Cancel
                  </button>
                  <button
                    onClick={runCommit}
                    disabled={busy || (preview.errors && preview.errors.length > 0) || (preview.rows_total === 0)}
                    className="px-4 py-2 rounded-md text-sm bg-foreground text-background font-medium hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="csv-modal-commit"
                  >
                    {busy ? "Committing…" : "Commit import"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* -------- Stage 3: result -------- */}
          {stage === "result" && result && (
            <div className="space-y-3" data-testid="csv-result-view">
              <div className="flex items-center gap-2 text-[#10B981]">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Import complete</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <MiniCounter label="Created" value={result.rows_created || 0} color="#10B981" />
                <MiniCounter label="Updated" value={result.rows_updated || 0} color="#3B82F6" />
                <MiniCounter label="Skipped" value={result.rows_skipped || 0} color="#71717A" />
              </div>
              {result.warnings && result.warnings.length > 0 && (
                <div className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-md p-2.5 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>{result.warnings.join(" · ")}</div>
                </div>
              )}
              {result.errors && result.errors.length > 0 && (
                <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5">
                  {result.errors.join(" · ")}
                </div>
              )}
              <div className="flex justify-end pt-1">
                <button onClick={close} className="px-4 py-2 rounded-md text-sm bg-foreground text-background font-medium" data-testid="csv-modal-done">
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
